import React, {useCallback, useEffect, useState} from 'react';
import Breadcrumbs from '../Components/Breadcrumbs';
import Table from '../Components/Tables';
import {ConfirmationProvider} from '../Components/ConfirmationContext';
import {AlertProvider} from '../Components/AlertContext';
import {formatDateUTC} from '../utils/formatDateUTC';

import {AuditOperationsApiProvider, useAuditOperationsApi} from "../Services/AuditOperationsApiProvider";
import {useNavigate, useParams} from "react-router-dom";
import Button from "../Components/Buttons";
import DateRangeSelector, {getDateRangeByPeriod} from "../Components/DateRangeSelector";

function AuditOperationsHub({canEdit}) {
  const {getAuditOperations, getAuditOperationTypes, exportAuditOperations } = useAuditOperationsApi();
  const { organizationId } = useParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [auditOperations, setAuditOperations] = useState([]);
  const [totalAuditOperations, setTotalAuditOperations] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const [searchOptions, setSearchOptions] = useState([]);

  const [selectedTypes, setSelectedTypes] = useState([]);

  const columns = [
    {name: 'ID', selector: (row) => row.key, sortable: true},
    {name: 'User Key', selector: (row) => row.user_key, sortable: true},
    {name: 'Operation Type', selector: (row) => row.operation_type, sortable: true},
    {name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: true},
    {name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: true},
  ];

  const handlePeriodChange = useCallback((period) => {
    setStartDate(period?.start_date);
    setEndDate(period?.end_date);
  }, []);

  const fetchAuditOperationTypes = async () => {
    try {
      return await getAuditOperationTypes();
    } catch (error) {
      console.error("Error fetching audit operation types:", error);
    }
  }

  const fetchAuditOperations = async (skip, limit, type=null) => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }
    try {
      setLoading(true);

      let start = startDate;
      let end = endDate;

      if (!start || !end) {
        const range = getDateRangeByPeriod("day");
        start = range.start_date;
        end = range.end_date;
      }

      const response = await getAuditOperations(skip, limit, start, end, type);
      setAuditOperations(response.items);
      setTotalAuditOperations(response.totalCount);
      setItemsPerPage(limit);
    } catch (error) {
      console.error("Error fetching audit operations:", error);
      setError("Failed to load audit operations");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAuditOperations(0, itemsPerPage);
  }, [startDate, endDate]);

  useEffect(() => {
    const loadTypes = async () => {
      const types = await fetchAuditOperationTypes();
      if (!types) return;
      const sortedTypes = [...types].sort((a, b) => a.localeCompare(b));
      const options = sortedTypes.map((type) => ({ label: type, value: type }));
      setSearchOptions(options);
    };
    loadTypes();
  }, []);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/audit-operations/${row.key}`);
  };

  const handleTypeChange = async (skip, limit, type) => {
    setSelectedTypes(Array.isArray(type) ? type : (type ? [type] : []));
    await fetchAuditOperations(skip, limit, type);
  };

const handleExport = async () => {
  try {
    let start = startDate;
    let end = endDate;

    if (!start || !end) {
      const range = getDateRangeByPeriod("day");
      start = range.start_date;
      end = range.end_date;
    }
    // якщо використовуєш мультиселект типів — передай сюди масив або null
    await exportAuditOperations(start, end, selectedTypes ?? null);
  } catch (error) {
    console.error("Error exporting audit operations:", error);
  }
};

  return (
    <div className="container">
      <Breadcrumbs/>
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          <div className={"mb-3 p-2"}>
            <DateRangeSelector onChange={handlePeriodChange}/>
          </div>
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="Audit Operations"
                columns={columns}
                data={auditOperations}
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={itemsPerPage}
                onRowClicked={handleRowClicked}
                paginationTotalRows={totalAuditOperations}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onChangePage={fetchAuditOperations}
                getRowUrl={(row) => `/organizations/${organizationId}/audit-operations/${row.key}`}
                onChangeFilter={handleTypeChange}
                typeSearch = 'multiselect'
                searchOptions={searchOptions}
                headerActions={
                  <Button
                    variant="primary"
                    size="medium"
                    onClick={handleExport}
                  >
                    Export JSON
                  </Button>
                }

              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AuditOperationsWrapper({canEdit}) {
  return (
    <AlertProvider>
      <ConfirmationProvider>
        <AuditOperationsApiProvider>
          <AuditOperationsHub canEdit={canEdit}/>
        </AuditOperationsApiProvider>
      </ConfirmationProvider>
    </AlertProvider>
  );
}

export default AuditOperationsWrapper;